import GuideLayout, {
  H2,
  H3,
  P,
  UL,
  OL,
  Callout,
  ExternalLink,
  Strong,
  Em,
} from "../ui/GuideLayout.jsx";

export default function GaimenKirikae() {
  return (
    <GuideLayout
      title="Carteira de motorista no Japão (gaimen kirikae)"
      subtitle="Conversão da habilitação brasileira para a japonesa. Passo a passo, com as novas regras de outubro de 2025."
      slug="gaimen-kirikae"
      description="Como converter a CNH brasileira em carteira japonesa via gaimen kirikae. Documentos, traduções JAF, provas teórica e prática após reforma de outubro 2025."
      updated="Maio de 2026"
      readTime="8 min"
    >
      <P>
        Antes de começar, uma notícia que muda tudo:{" "}
        <Strong>desde outubro de 2025</Strong>, a conversão de carteira
        estrangeira ficou consideravelmente mais difícil. As taxas de aprovação
        caíram. Quem for fazer agora precisa se preparar de verdade — não dá
        mais pra ir &ldquo;na sorte&rdquo;.
      </P>

      <H2 id="brasil-tem-acordo">O Brasil tem acordo com o Japão?</H2>
      <Callout kind="warn">
        <Strong>Não.</Strong> O Brasil não está entre os países com acordo de
        reciprocidade. Brasileiros precisam fazer o processo completo: prova
        teórica + prova prática. Não tem como pular.
      </Callout>
      <P>
        Os países com acordo (que ficam dispensados das provas) são
        principalmente da Europa, Coreia do Sul, Taiwan, Austrália, Nova
        Zelândia e alguns estados dos EUA. Brasil, Filipinas, China, Índia,
        Vietnã e Tailândia ficam de fora — fazem o processo completo.
      </P>

      <H2 id="pre-requisitos">Pré-requisitos</H2>
      <P>Antes de qualquer coisa, você precisa:</P>
      <UL>
        <li>
          <Strong>CNH brasileira válida</Strong>, com data de emissão original
          (não a renovação) há pelo menos 3 meses antes de você se mudar para
          o Japão.
        </li>
        <li>
          <Strong>Comprovar que você ficou no Brasil por pelo menos 3 meses
          após emitir a CNH</Strong>. Isso é provado com carimbos de
          passaporte. Se sua CNH foi emitida em janeiro e você veio pro Japão
          em fevereiro do mesmo ano, vai dar problema.
        </li>
        <li>
          <Strong>Ser residente legal do Japão</Strong> com juuminhyou
          (証明書 / 住民票). Turista não pode mais — essa regra também é da
          reforma de outubro de 2025.
        </li>
        <li>
          <Strong>Visão de 0.7 ou mais</Strong> nos dois olhos (com ou sem
          óculos). Eles fazem o teste no dia.
        </li>
      </UL>

      <H2 id="documentos">Documentos para levar</H2>
      <UL>
        <li>
          <Strong>CNH brasileira original</Strong> (válida).
        </li>
        <li>
          <Strong>Tradução oficial da CNH</Strong> feita pela{" "}
          <ExternalLink href="https://english.jaf.or.jp/individual-services/driver-license-translation">
            JAF (Japan Automobile Federation)
          </ExternalLink>{" "}
          ou pelo consulado brasileiro. Custo JAF: ¥4.000. Processo agora é
          online — você acessa só de dentro do Japão.
        </li>
        <li>
          <Strong>Passaporte atual + passaportes antigos</Strong> com todos os
          carimbos de entrada e saída para comprovar os 3 meses no Brasil
          após emissão da CNH.
        </li>
        <li>
          <Strong>Juuminhyou (住民票)</Strong> recente. Importante: peça com a
          opção <Em>tokutei jikou ga kisai sareta mono</Em> (特定事項が記載された
          もの — &ldquo;com itens específicos&rdquo;), que inclui as
          informações necessárias para a licença.
        </li>
        <li>
          <Strong>Zairyuu card</Strong> (cartão de residência).
        </li>
        <li>
          <Strong>Foto 3x2,4cm</Strong> (algumas prefeituras tiram no local,
          outras pedem que você leve).
        </li>
        <li>
          <Strong>Inkan</Strong> (carimbo pessoal).
        </li>
        <li>
          <Strong>Dinheiro</Strong>: ¥4.000 a ¥5.000 dependendo da prefeitura.
        </li>
      </UL>

      <H2 id="processo">O processo passo a passo</H2>

      <H3>1. Agende no menkyo center (運転免許センター)</H3>
      <P>
        Cada prefeitura tem seu próprio menkyo center com regras e horários
        específicos. <Strong>Marque com 2 a 4 meses de antecedência</Strong> —
        depois da reforma, a procura aumentou e as vagas voam.
      </P>
      <P>
        Em muitas prefeituras o agendamento é online em japonês. Se você não
        consegue navegar, peça ajuda a um amigo ou ligue (vai ser atendido em
        japonês, prepare-se).
      </P>

      <H3>2. Faça a tradução da CNH</H3>
      <P>
        Tradução pela JAF é a mais aceita. Desde março de 2025, só dá pra
        fazer pelo sistema online (de dentro do Japão). Sobe foto da frente e
        verso da CNH, paga ¥4.000, e recebe o documento pronto.
      </P>

      <H3>3. Dia da triagem documental e teste de visão</H3>
      <P>
        Chegue cedo (recomendado 1h antes da hora marcada). Funcionários
        conferem todos os documentos, fazem teste de vista. Pague a taxa.
        Depois te entregam o agendamento para as provas.
      </P>
      <P>
        Em muitos centros, a prova teórica é no mesmo dia. A prova prática
        normalmente fica para uma data posterior.
      </P>

      <H3>4. Prova teórica (50 questões)</H3>
      <P>
        <Strong>Esta é a maior mudança da reforma:</Strong> antes era um
        teste de 10 questões verdadeiro/falso (7 acertos para passar). Agora
        são <Strong>50 questões com 90% de acerto</Strong> exigido (45 corretas
        em 50).
      </P>
      <P>
        Disponível em vários idiomas, geralmente incluindo português. Confirme
        no seu menkyo center antes — não são todos que oferecem português.
      </P>
      <P>Conteúdo:</P>
      <UL>
        <li>Sinais de trânsito japoneses.</li>
        <li>Regras de prioridade e preferência de via.</li>
        <li>Áreas de estacionamento proibido.</li>
        <li>Velocidades máximas por tipo de via.</li>
        <li>Procedimentos em caso de acidente.</li>
      </UL>
      <P>
        <Strong>Como estudar:</Strong> apostilas de <Em>karimen</Em>{" "}
        (carteira provisória japonesa). Conteúdo é o mesmo. Você acha em
        lojas de livros usados ou online. JAF também publica material em
        idiomas estrangeiros.
      </P>

      <H3>5. Prova prática (em circuito fechado)</H3>
      <P>
        Esta é a parte onde mais gente reprova — e ficou ainda mais rigorosa
        em outubro de 2025. Você dirige um carro do menkyo center em um
        circuito interno seguindo as orientações do examinador.
      </P>
      <P>
        Você começa com 100 pontos. Precisa terminar com 70 ou mais. Erros
        descontam de -5 a -20 pontos, ou causam reprovação imediata em casos
        graves.
      </P>
      <P>O que examinadores observam (e foi reforçado pós-2025):</P>
      <UL>
        <li>
          <Strong>Checagem de segurança exagerada</Strong> — olhar por cima do
          ombro ao mudar de faixa, virar a cabeça em interseções, conferir
          retrovisores constantemente.
        </li>
        <li>
          <Strong>Parar completamente em STOP signs</Strong> — pneu parado por
          1 segundo no mínimo. Quase-parada é reprovação.
        </li>
        <li>
          <Strong>Velocidade adequada</Strong> — nem rápido demais (perde
          pontos) nem lento demais (também perde pontos).
        </li>
        <li>
          <Strong>Posicionamento na pista</Strong> — manter a esquerda em
          ruas estreitas, fazer curvas amplas em interseções largas.
        </li>
        <li>
          <Strong>Confirmação de pedestres e ciclistas</Strong> antes de fazer
          conversão.
        </li>
      </UL>

      <Callout>
        <Strong>Dica que faz diferença:</Strong> Os movimentos têm que ser
        <Em> exagerados a ponto do examinador ver claramente</Em>. Se você
        olha o retrovisor com o canto do olho — não conta. Vire a cabeça.
        Pareça meio estranho. É assim que se passa.
      </Callout>

      <H3>6. Recebendo a habilitação japonesa</H3>
      <P>
        Aprovado? Você tira foto na hora, paga a taxa de emissão, e leva sua
        habilitação japonesa na mão. Geralmente no mesmo dia.
      </P>
      <P>
        Reprovado? Você marca nova tentativa para a parte que reprovou. Não
        precisa refazer a parte aprovada.
      </P>

      <H2 id="custos">Custos totais (estimativa)</H2>
      <UL>
        <li>Tradução JAF: ¥4.000</li>
        <li>Taxa de exame (1ª vez): ¥4.000 a ¥5.000</li>
        <li>Taxa de emissão da carteira: ¥2.000 a ¥2.500</li>
        <li>
          Se reprovar e refazer prática: ¥2.500 a ¥3.000 por tentativa
        </li>
        <li>
          Aula prática preparatória (opcional mas recomendada): ¥6.000 a
          ¥10.000 por hora
        </li>
      </UL>
      <P>
        Total típico passando na primeira: <Strong>~¥10.000</Strong>. Caso
        comum de 2-3 reprovações na prática: <Strong>até ¥20.000-25.000</Strong>.
      </P>

      <H2 id="dicas">Dicas práticas de quem já passou</H2>
      <OL>
        <li>
          <Strong>Pague uma aula no centro de treinamento</Strong> antes da
          prova prática. Custa ¥6-10mil por hora mas ensina exatamente os
          gestos específicos que examinadores querem ver. Muitos consideram
          isso quase obrigatório agora.
        </li>
        <li>
          <Strong>Não se distraia com o GPS ou painel</Strong> durante a
          prova. Mãos firmes no volante, olhar pra frente e nas checagens.
        </li>
        <li>
          <Strong>Use luz de seta cedo</Strong> — pelo menos 30 metros antes
          da conversão.
        </li>
        <li>
          <Strong>Vá vestido sóbrio</Strong> — chinelo, regata e bermuda
          podem influenciar. Calça comprida, camisa, sapato fechado.
        </li>
        <li>
          <Strong>Estude o mapa do circuito</Strong> — você pode pedir o
          layout antes. Saber as conversões antecipadamente economiza pontos
          em hesitação.
        </li>
      </OL>

      <H2 id="conclusao">Resumo prático</H2>
      <P>
        Brasileiros precisam fazer prova teórica (50 questões, 90% acerto) e
        prova prática completa. Reforma de outubro de 2025 tornou tudo
        significativamente mais rígido. Reserve <Strong>6 meses</Strong>{" "}
        desde o agendamento até receber a carteira, considere{" "}
        <Strong>aula preparatória</Strong> para a prova prática, e leve todos
        os documentos exatos para não perder a viagem.
      </P>
      <P>
        Para informações específicas da sua prefeitura, contate o menkyo
        center local. Cada um tem suas particularidades de horário, idiomas
        oferecidos na prova teórica, e detalhes de procedimento.
      </P>
    </GuideLayout>
  );
}
